import { useEffect, useState } from "react";
import {
  fetchProfile,
  fetchSession,
  type Profile,
  type SessionState,
  type SessionUser,
} from "./api.ts";
import { Layout, Shell } from "./components/Layout.tsx";
import { LoginHistoryPage } from "./pages/LoginHistoryPage.tsx";
import { ProductsPage } from "./pages/ProductsPage.tsx";
import { ProfilePage } from "./pages/ProfilePage.tsx";
import { SecurityPage } from "./pages/SecurityPage.tsx";
import { ROUTES, usePath } from "./router.ts";
import { useLoader } from "./use-loader.ts";

type Props = {
  /** 未ログイン時の遷移。テストで差し替える */
  onLoginRequired?: (loginUrl: string) => void;
};

// 既定値を関数の外で定義する。レンダーごとに新しい関数を作ると useEffect が毎回動き、
// /api/session を無限に呼んでしまう
const redirectToLogin = (url: string) => window.location.assign(url);

const TITLES = {
  [ROUTES.products]: "プロダクト",
  [ROUTES.profile]: "基本設定",
  [ROUTES.security]: "ログインとセキュリティ",
  [ROUTES.loginHistory]: "ログイン記録",
} as const;

export function App({ onLoginRequired = redirectToLogin }: Props) {
  const [session, setSession] = useState<SessionState | { status: "loading" }>({
    status: "loading",
  });

  useEffect(() => {
    let active = true;
    fetchSession().then((state) => {
      if (!active) return;
      setSession(state);
      if (state.status === "unauthenticated") onLoginRequired(state.loginUrl);
    });
    return () => {
      active = false;
    };
  }, [onLoginRequired]);

  switch (session.status) {
    case "loading":
      return <Message title="読み込んでいます…" />;
    case "authenticated":
      return <AuthenticatedApp user={session.user} />;
    case "unauthenticated":
      return (
        <Message title="ログイン画面へ移動しています">
          移動しない場合は <a href={session.loginUrl}>ログイン</a> を押してください。
        </Message>
      );
    case "ip_not_allowed":
      return (
        <Message title="このネットワークからは利用できません">
          所属組織の管理者が許可したネットワークから、もう一度アクセスしてください。
          {session.ip !== null ? (
            <>
              <br />
              現在の IP アドレス: <code>{session.ip}</code>
            </>
          ) : null}
        </Message>
      );
    case "not_configured":
      return (
        <Message title="ログインの設定がありません">
          このサーバーには Auth0 の設定がありません（.env の AUTH0_* と APP_BASE_URL）。
        </Message>
      );
    case "error":
      return (
        <Message title="ログイン状態を取得できませんでした">
          時間をおいて、ページを再読み込みしてください。
        </Message>
      );
  }
}

function AuthenticatedApp({ user }: { user: SessionUser }) {
  const path = usePath();
  const profile = useLoader<Profile>(fetchProfile);

  useEffect(() => {
    document.title = `${TITLES[path]} | Sprocket アカウント設定`;
  }, [path]);

  const loaded = profile.state.status === "loaded" ? profile.state.data : null;
  const userName = loaded?.displayName ?? user.name ?? user.email ?? "";
  const picture = loaded !== null ? loaded.picture : user.picture;

  return (
    <Layout path={path} userName={userName} picture={picture}>
      {path === ROUTES.products ? <ProductsPage /> : null}
      {path === ROUTES.profile ? (
        <ProfilePage
          profile={profile.state}
          onChange={(data) => profile.setState({ status: "loaded", data })}
          onRetry={profile.reload}
        />
      ) : null}
      {path === ROUTES.security ? <SecurityPage user={user} /> : null}
      {path === ROUTES.loginHistory ? <LoginHistoryPage /> : null}
    </Layout>
  );
}

/** ログイン前・拒否などのナビの無い画面 */
function Message({ title, children }: { title: string; children?: React.ReactNode }) {
  return (
    <Shell>
      <main className="message">
        <div className="message__box" role={children === undefined ? "status" : undefined}>
          <h1>{title}</h1>
          {children !== undefined ? <p>{children}</p> : null}
        </div>
      </main>
    </Shell>
  );
}
